import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getUserProfile, UserProfile } from '../services/profileService';
import { hasPendingDiagnostic, cleanupPendingDiagnostic } from '../utils/pendingDiagnosticStorage';
import { savePendingDiagnosticForUser, linkClientToDiagnostic } from '../services/diagnosticService';
import toast from 'react-hot-toast';

export type { UserProfile } from '../services/profileService';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  refreshProfile: () => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  login: (email: string, password: string) => Promise<{ savedDiagnosticId?: string }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Compteur de génération : chaque fetch porte un id unique.
  // Si un fetch plus récent démarre avant qu'un ancien se termine,
  // le résultat de l'ancien est ignoré (anti race-condition).
  const profileFetchId = useRef(0);

  const refreshProfile = async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setUserProfile(null); return; }
    // Utiliser le même compteur de génération qu'onAuthStateChange pour éviter
    // qu'un fetch concurrent (ex: token-refresh Supabase) n'écrase le profil frais.
    const fetchId = ++profileFetchId.current;
    try {
      const profile = await getUserProfile(user.id);
      if (fetchId === profileFetchId.current) {
        setUserProfile(profile);
      }
    } catch {
      if (fetchId === profileFetchId.current) {
        setUserProfile(null);
      }
    }
  };

  async function signup(email: string, password: string, _firstName: string, _lastName: string): Promise<void> {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    // Profile is created automatically by the on_auth_user_created DB trigger (SECURITY DEFINER)
  }

  async function login(email: string, password: string): Promise<{ savedDiagnosticId?: string }> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data.user && hasPendingDiagnostic()) {
      try {
        const result = await savePendingDiagnosticForUser(data.user.id);
        if (result.success && result.diagnosticId) {
          return { savedDiagnosticId: result.diagnosticId };
        } else {
          console.error('Failed to save pending diagnostic:', result.error);
          return {};
        }
      } catch (error) {
        console.error('Error saving pending diagnostic after login:', error);
        return {};
      }
    }

    return {};
  }

  async function logout(): Promise<void> {
    setUserProfile(null); // nettoyage immédiat avant que Supabase confirme la déconnexion
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  async function resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`
    });
    if (error) throw error;
  }

  async function updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setCurrentUser(user);
      if (user) {
        // Même mécanisme anti race-condition que onAuthStateChange :
        // si un événement onAuthStateChange se déclenche entre-temps et incrémente
        // le compteur, le résultat de ce fetch sera ignoré au profit du plus récent.
        const fetchId = ++profileFetchId.current;
        getUserProfile(user.id)
          .then(profile => {
            if (fetchId === profileFetchId.current) setUserProfile(profile);
          })
          .catch(() => {
            if (fetchId === profileFetchId.current) setUserProfile(null);
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // handled downstream
      }
      const user = session?.user ?? null;
      setCurrentUser(user);
      // Nettoyage immédiat : on n'affiche jamais un profil périmé
      // pendant que le nouveau fetch est en cours.
      setUserProfile(null);

      if (user) {
        // Incrémenter le compteur de génération pour cette tentative de fetch.
        // Si onAuthStateChange se déclenche à nouveau avant la fin de ce fetch
        // (ex: logout puis login rapide), l'ancien résultat sera ignoré.
        const fetchId = ++profileFetchId.current;

        // Pour SIGNED_IN : délai de 500 ms pour s'assurer que la session Supabase
        // est complètement établie côté PostgREST avant d'appeler getUserProfile.
        const delay = event === 'SIGNED_IN'
          ? new Promise<void>(resolve => setTimeout(resolve, 500))
          : Promise.resolve();

        delay
          .then(() => getUserProfile(user.id))
          .then(profile => {
            if (fetchId === profileFetchId.current) {
              setUserProfile(profile);
              // Si c'est un client invité, lier son compte au diagnostic
              // désigné dans ses métadonnées (fire-and-forget, idempotent).
              if (profile?.role === 'client') {
                const diagnosticId = user.user_metadata?.diagnostic_id;
                if (diagnosticId) {
                  linkClientToDiagnostic(diagnosticId, user.id).catch(console.error);
                }
              }
            }
          })
          .catch((err) => {
            console.error(`[AuthContext] fetchId=${fetchId} getUserProfile error:`, err);
            if (fetchId === profileFetchId.current) {
              setUserProfile(null);
            }
          });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    currentUser,
    userProfile,
    refreshProfile,
    signup,
    login,
    logout,
    resetPassword,
    updatePassword,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
